import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { VoicemailConfigApi } from '@frontend/api-voicemail-config';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { DropdownField, FormFieldActionTypes, SkeletonLoader, Text, useForm } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { getNameWithoutExtension } from '../../../utils/phone-utils';
import { AudioQueryMedia, useAudioQuery } from '../../../utils/use-audio-query';
import { AudioPicker, findMediaItem } from '../../audio-picker/audio-picker';
import { AudioItem } from '../../audio-picker/types';
import { voicemailAllowedOptions } from '../../call-routes/call-route-details/add-step-panel/voicemail-step';
import { CachedAudioScrubber } from '../../common/cached-audio-scrubber';
import { VoicemailInstruction } from '../types';
import { ReadOnlyInstructionField } from './read-only-instruction';
import { InstructionFieldStyles } from './styles';

const VOICEMAIL_PROMPT_BREAKPOINT_WIDTH = 750;

// Helper functions for better clarity
const getInitialGreetingValue = (state: VoicemailInstruction): string => {
  if (state.data.voicemailGreetingId) return state.data.voicemailGreetingId;
  if (state.data.systemMediaId) return state.data.systemMediaId;
  if (state.data.skipGreeting) return 'none';
  return 'default';
};

const isAudioLibraryFile = (greetingId: string, audioData?: AudioQueryMedia): boolean => {
  return !!(
    audioData?.customMedia.some((audio) => audio.id === greetingId) ||
    audioData?.standardMedia.some((audio) => audio.id === greetingId)
  );
};

const buildVoicemailPayload = (
  baseState: VoicemailInstruction,
  voicemailBoxId: string,
  greetingId: string,
  audioData?: AudioQueryMedia
): VoicemailInstruction => {
  const payload: VoicemailInstruction = {
    ...baseState,
    id: voicemailBoxId,
    data: {
      voicemailBoxId,
      voicemailGreetingId: '',
      systemMediaId: '',
      skipGreeting: false,
    },
  };

  // Audio library file selected
  if (isAudioLibraryFile(greetingId, audioData)) {
    payload.data.systemMediaId = greetingId;
    payload.data.skipGreeting = true;
    return payload;
  }

  // No greeting selected
  if (greetingId === 'none') {
    payload.data.skipGreeting = true;
    return payload;
  }

  // Default greeting (voicemailGreetingId = '', systemMediaId = '', skipGreeting = false)
  if (greetingId === 'default') {
    return payload;
  }

  // Specific voicemail box greeting
  payload.data.voicemailGreetingId = greetingId;
  return payload;
};

const shouldShowGreetingPlayer = (state: VoicemailInstruction): boolean => {
  // Show player for: specific greeting, audio library file, or default greeting
  return (
    !!state.data.voicemailGreetingId ||
    !!state.data.systemMediaId ||
    (!state.data.voicemailGreetingId && !state.data.systemMediaId && !state.data.skipGreeting)
  );
};

export const VoicemailPromptInstructionFields = ({
  onChange,
  instructionState,
  disabled = false,
  isReadOnly = false,
}: {
  onChange: (payload: VoicemailInstruction) => void;
  instructionState: VoicemailInstruction;
  disabled?: boolean;
  isReadOnly?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const location = accessibleLocationData[selectedLocationIds[0]];
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? location.phoneTenantId ?? '';
  // Track selected audio file to determine if it was a mailbox numbered greeting
  const [selectedAudioFile, setSelectedAudioFile] = useState<AudioItem | undefined>(undefined);

  const { data: voicemailBoxes = [], isLoading: isLoadingBoxes } = useQuery({
    queryKey: queryKeys.listTenantVoicemailBoxes(tenantId),
    queryFn: () => VoicemailConfigApi.listVoicemailBoxes({ tenantId: tenantId }),
    select: (data) => {
      return data.voicemailBoxes
        .sort((a, b) => a.mailbox.name.localeCompare(b.mailbox.name))
        .map((voicemailBox) => ({
          value: voicemailBox.mailbox.id,
          label: voicemailBox.mailbox.name,
        }));
    },
    enabled: !!tenantId && !isReadOnly,
  });

  const { data: audioFiles, isLoading: isLoadingAudio } = useAudioQuery({
    tenantId: settingsTenantLocation?.phoneTenantId ?? '',
    mailboxId: instructionState.id,
    allowedOptions: {
      custom: true,
      standard: true,
      mailbox: true,
    },
  });

  const form = useForm({
    fields: {
      voicemailBoxId: {
        type: 'dropdown',
        value: instructionState.id,
      },
      voicemailGreetingId: {
        type: 'dropdown',
        value: getInitialGreetingValue(instructionState),
      },
    },
    fieldStateReducer: (state, action) => {
      if (action.type !== FormFieldActionTypes.Update) {
        return state;
      }

      const voicemailBoxId = state.voicemailBoxId.value;
      const greetingId = state.voicemailGreetingId.value;

      // Build the updated payload based on current form values
      let updatedPayload = buildVoicemailPayload(instructionState, voicemailBoxId, greetingId, audioFiles);

      // Special Case:
      // If there was a change to the voicemail box and the previous selected greeting was a
      // mailbox-specific numbered greeting, then reset to the default greeting since the mailbox numbered
      // greeting is no longer valid for the new mailbox.
      //
      // NOTE: this logic works because the this fieldStateReducer is called when one of the fields changes so
      // we can rely on the selectedAudioFile state to determine if the previous greeting was a mailbox greeting since that
      // state is updated via the AudioPicker onSelect callback, however, if we need to use the isMailboxGreeting value
      // for logic specific to the voicemailGreetingId field change, then we would need to implement additional logic to track that.
      const isMailboxGreeting = selectedAudioFile?.type === 'mailbox';
      const isVoicemailBoxChanged = voicemailBoxId !== instructionState.id;
      if (action.payload.name === 'voicemailBoxId' && isMailboxGreeting && isVoicemailBoxChanged) {
        updatedPayload = buildVoicemailPayload(instructionState, voicemailBoxId, 'default', audioFiles);

        // Update form state to reflect the reset
        onChange(updatedPayload);
        return {
          ...state,
          voicemailGreetingId: {
            ...state.voicemailGreetingId,
            value: 'default', // update form field to 'default'
          },
        };
      }

      // Notify parent of changes
      onChange(updatedPayload);
      return state;
    },
  });

  const shouldShowAudioScrubber = shouldShowGreetingPlayer(instructionState);

  const currentAudioFile = useMemo(() => {
    let mediaId = instructionState.data.voicemailGreetingId || instructionState.data.systemMediaId || '';

    if (!mediaId && instructionState.data.skipGreeting === false) {
      // If voicemailGreetingId and systemMediaId are not set, and skipGreeting is false, then it's the default greeting
      mediaId = 'default';
    }

    const foundMedia = findMediaItem(mediaId, audioFiles?.allMedia ?? []);

    return foundMedia;
  }, [
    audioFiles?.allMedia,
    instructionState.data.systemMediaId,
    instructionState.data.voicemailGreetingId,
    instructionState.data.skipGreeting,
  ]);

  if (isLoadingBoxes || isLoadingAudio) {
    return <SkeletonLoader height={40} width={225} />;
  }

  if (isReadOnly) {
    return (
      <div
        css={css`
          max-width: 800px;
          display: flex;
          gap: ${theme.spacing(2)};
          align-items: center;
          flex-wrap: wrap;
        `}
      >
        <ReadOnlyInstructionField
          instructionName={instructionState.name ?? t('Unknown Voicemail')}
          iconName={'voicemail'}
          instructionType={t('Voicemail')}
        />
        <div
          css={css`
            display: flex;
            gap: ${theme.spacing(2)};
            align-items: center;
          `}
        >
          <Text
            css={css`
              @media (max-width: ${VOICEMAIL_PROMPT_BREAKPOINT_WIDTH}px) {
                align-self: flex-start;
                position: relative;
                top: ${theme.spacing(1)};
              }
            `}
          >
            {'+'}
          </Text>
          <ReadOnlyInstructionField
            instructionName={getNameWithoutExtension(currentAudioFile?.name) || t('No Greeting')}
            iconName={'speaker'}
            instructionType={t('Voicemail Greeting')}
          />
          <CachedAudioScrubber
            css={{
              alignSelf: 'start',
            }}
            singlePlayer
            mediaId={currentAudioFile?.id || ''}
            filePath={currentAudioFile?.path || ''}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      css={css`
        max-width: 800px;
        display: flex;
        gap: ${theme.spacing(2)};
        align-items: center;
        flex-wrap: wrap;
      `}
    >
      <DropdownField
        {...form.getFieldProps('voicemailBoxId')}
        name={'voicemailBoxId'}
        label={t('Voicemail Box')}
        css={InstructionFieldStyles}
        disabled={disabled}
      >
        {voicemailBoxes?.map((voicemailBox) => (
          <DropdownField.Option key={voicemailBox.value} value={voicemailBox.value}>
            {voicemailBox.label}
          </DropdownField.Option>
        ))}
      </DropdownField>
      <div
        css={css`
          display: flex;
          gap: ${theme.spacing(2)};
          align-items: center;
        `}
      >
        <Text
          css={css`
            @media (max-width: ${VOICEMAIL_PROMPT_BREAKPOINT_WIDTH}px) {
              align-self: flex-start;
              position: relative;
              top: ${theme.spacing(1)};
            }
          `}
        >
          {'+'}
        </Text>
        <AudioPicker
          field={form.getFieldProps('voicemailGreetingId')}
          name={'voicemailGreetingId'}
          enableLoader
          disabled={disabled}
          onSelect={(item) => {
            // NOTE: this callback function will not be called if the user selects the "No Greeting option". The
            // value will still be set on the field (form.values), but the onSelect function will not
            // be called.
            setSelectedAudioFile(item);
          }}
          css={css`
            display: flex;
            & > div {
              min-width: 230px;
              max-width: 250px;
            }

            @media (max-width: ${VOICEMAIL_PROMPT_BREAKPOINT_WIDTH}px) {
              flex-wrap: wrap;
            }
          `}
          mailboxId={instructionState.id}
          tenantId={settingsTenantLocation?.phoneTenantId ?? ''}
          // Use the same allowed options as in the voicemail step in the call route details page
          // so that users have a consistent experience. If a user can select a certain option
          // when configuring a voicemail prompt in a call route, they should be able to select
          // the same option when configuring a voicemail prompt in the phone tree, or else we won't
          // be able to display the selected greeting name correctly when editing here.
          allowedOptions={voicemailAllowedOptions}
          widths={{ field: 225, scrubber: shouldShowAudioScrubber ? 200 : 0 }}
        />
      </div>
    </div>
  );
};
